import React from 'react';
import { useGameStore } from '../store/gameStore';
import { ArrowUp, ArrowDown, MousePointer, X, ChevronRight, Target, ShieldCheck, Activity, Terminal, Radio, Zap } from 'lucide-react';
import clsx from 'clsx';

export const TutorialOverlay: React.FC = () => {
  const tutorialStep = useGameStore(state => state.tutorialStep);
  const nextTutorialStep = useGameStore(state => state.nextTutorialStep);
  const skipTutorial = useGameStore(state => state.skipTutorial);
  const currentScenario = useGameStore(state => state.currentScenario);
  const sectors = useGameStore(state => state.sectors);

  if (tutorialStep === 0) return null;

  const getSteps = () => {
    if (!currentScenario) return [];

    // Match both legacy 'l1-' prefix and current 'tutorial-' prefix
    const isTutorialScenario =
      currentScenario.id.startsWith('l1-') ||
      currentScenario.id.startsWith('tutorial-');

    if (isTutorialScenario) {
       // Use targetSectorIds (the real field name on MissionScenario)
       const targetId = currentScenario.targetSectorIds?.[0];
       const targetName = sectors.find(s => s.id === targetId)?.name || 'Target Sector';
       
       return [
         {
           title: currentScenario.name,
           text: currentScenario.description,
           position: "top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2",
           arrow: null,
           icon: <ShieldCheck className="w-6 h-6 text-blue-400" />
         },
         {
           title: "Select Target",
           text: `Click on ${targetName} to select it on the map.`,
           position: "top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2",
           arrow: null,
           icon: <MousePointer className="w-6 h-6 text-blue-400" />
         },
         {
           title: "Execute Mission",
           text: `Select an action from the right panel and click EXECUTE to apply it to the sector.`,
           position: "bottom-32 left-1/2 -translate-x-1/2",
           arrow: <ArrowDown className="mx-auto animate-bounce mb-2 text-blue-400" />,
           icon: <Activity className="w-6 h-6 text-blue-400" />
         }
       ];
    }

    // Fallback or other scenarios
    return [
        {
          title: "Mission Briefing",
          text: currentScenario.description,
          position: "top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2",
          arrow: null,
          icon: <Target className="w-6 h-6 text-blue-400" />
        }
    ];
  };

  const steps = getSteps();
  const currentStep = steps[tutorialStep - 1];

  if (!currentStep) return null;

  return (
    <div className="fixed inset-0 z-[100] pointer-events-none">
      {/* Dimmed Background */}
      <div className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm transition-opacity duration-500" />

      {/* Tutorial Card */}
      <div className={clsx(
        "absolute pointer-events-auto transition-all duration-500 ease-out max-w-md w-full",
        currentStep.position
      )}>
        <div className="glass-panel p-6 rounded-2xl border border-ops-blue/30 shadow-[0_0_50px_rgba(59,130,246,0.2)] relative overflow-hidden group">
          
          {/* Decorative Elements */}
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-ops-blue to-transparent opacity-50" />
          <div className="absolute -left-10 -top-10 w-32 h-32 bg-ops-blue/10 rounded-full blur-3xl group-hover:bg-ops-blue/20 transition-colors" />

          <div className="relative z-10">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-ops-blue/10 rounded-lg border border-ops-blue/20">
                  {currentStep.icon}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white tracking-wide">{currentStep.title}</h3>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs font-mono text-ops-blue uppercase tracking-wider">Step {tutorialStep}/{steps.length}</span>
                    <div className="h-px w-12 bg-ops-blue/30" />
                  </div>
                </div>
              </div>
              <button 
                onClick={skipTutorial}
                className="text-slate-400 hover:text-white transition-colors p-1 hover:bg-white/10 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-slate-300 leading-relaxed mb-6 border-l-2 border-ops-blue/30 pl-4">
              {currentStep.text}
            </p>

            {currentStep.arrow}

            <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/5">
              <button 
                onClick={skipTutorial}
                className="text-xs text-slate-500 hover:text-slate-300 uppercase tracking-widest font-mono transition-colors"
              >
                Dismiss
              </button>
              
              {tutorialStep < steps.length && (
                <button 
                  onClick={nextTutorialStep}
                  className="flex items-center gap-2 px-4 py-2 bg-ops-blue/20 hover:bg-ops-blue/30 text-ops-blue rounded-lg border border-ops-blue/30 transition-all group/btn"
                >
                  <span className="text-sm font-bold">Next</span>
                  <ChevronRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
